import ResponseHelper from './response-helper';
import mockDataMyCourses from '../../mock-data/my-courses.json';
/**
 * Lib that manages communication with Vizzto server related to content (categories, genres, video content...).
 * Returns with `code = 0` are considered sucessfull.
 */

/**
 * @typedef FieldErrorResponse All methods can return a failed promise with field validation errors following this structure.
 * @type {Object}
 * @property {Number} code Field validation error code (400)
 * @property {String} message Global error message (like: "Some fields are invalid.").
 * @property {Object} data Holds the errors. Each property is related to a field error. Property's key must be the field name. Property's value must be the error message.
 * @example { code: 400, message: "Request is invalid.", data: { user: "User cannot have more than 3 chars.", password: "Password doesn't have special char." } }
 */

const assetsArray = [];
const quizArray = [];
const certificateArray = [];

function contentMapper(item) {
  if (item.assets) {
    item.assets.forEach((item) => {
      assetsArray.name = item.name;
      assetsArray.url = item.url;
      assetsArray.id = item.id;
    });
  } else {
    item.assets = [];
  }

  if (item.certificates) {
    item.certificates.forEach((item) => {
      certificateArray.id = item.id;
      certificateArray.name = item.name;
      certificateArray.url = item.url;
    });
  } else {
    item.certificates = [];
  }

  return {
    canOpenDetails: item.playPermissionCode !== 18,
    id: item.id,
    title: item.name,
    descriptions: {
      short: item.shortDescription,
      long: item.description
    },
    learningGoal: item?.learningGoal,
    targetAudience: item?.targetAudience,
    completionCriteria: item?.completionCriteria,
    genres: item.genres,
    userRating: item.userEvaluation,
    images: {
      screenbug: item.featuredChannelImage,
      thumbs: {
        portrait: [item.featuredImage],
        landscape: [item.regularImage, item.carouselImage]
      },
      featured: {
        portrait: '',
        landscape: item.backgroundImage
      }
    },
    published: item.year,
    availability: {
      plans: item.plans,
      rent: item.price || 0
    },
    specifics: {
      duration: item.duration,
      ageRating: item.rating,
      cast: item.cast,
      svod: item.svod,
      tvod: item.tvod,
      contentType: item.contentType,
      hasTeaser: item.hasTeaser,
      teaserBaseUrl: item.teaserBaseUrl,
      isHappening: item.isHappening,
      timeToNextEvent: item.timeToNextEvent,
      genres: item.genres,
      year: item.year,
      seasons: item.seasons,
      seasonNumber: item.seasonNumber,
      playedRatio: item.watchedPercentage / 100,
      hasAttachments: item.assets.length,
      questionnaireId: item.questionnaireId,
      allDone: item.watched, // watched          // Whether user watched all content and did everything related to it (like tests or answering pools).
      hasQuiz: item.questionnaireId, // item.questionnaires.length,           // Whether any content within has has a quiz related to it.
      certificate: {
        url: item.certificateUrl
      },
      chatId: item.chatId,
      score: item.score || 0, // User's score on this content. In courses, the score would be points. If there's no score data, its value should be 0.
      totalHours: item.totalHours || 0,
      totalLessons: item.totalLessons || 0,
      totalPoints: item.totalPoints || 0
    },
    attachments: assetsArray,
    quizzes: quizArray,
    certificates: certificateArray,
    access: {
      ppc: item.playPermissionCode,
      firstPredecessorName: item?.predecessors?.[0]?.name,
      message: ''
    },
    inFavorites: !!item.inPlaylist,
    classroom: !item.classroom
      ? {}
      : {
          ...item.classroom,
          registeredUsers: item.classroom.registeredUsers
            ? item.classroom.registeredUsers
            : 0
        }
  };
}

function contentDetailMapper(item) {
  if (item.assets) {
    item.assets.forEach((item) => {
      assetsArray.name = item.name;
      assetsArray.url = item.url;
      assetsArray.id = item.id;
    });
  } else {
    item.assets = [];
  }

  return {
    id: item.id,
    title: item.name,
    descriptions: {
      short: item.shortDescription,
      long: item.description
    },
    published: item.year,
    availability: {
      plans: item.plans,
      rent: item.price || 0
    },
    specifics: {
      duration: item.duration,
      ageRating: item.rating,
      cast: item.cast,
      svod: item.svod,
      tvod: item.tvod,
      contentType: item.contentType,
      hasTeaser: item.hasTeaser,
      teaserBaseUrl: item.teaserBaseUrl,
      isHappening: item.isHappening,
      timeToNextEvent: item.timeToNextEvent,
      genres: item.genres,
      year: item.year,
      seasonNumber: item.seasonNumber,
      playedRatio: item.watchedPercentage / 100,
      allDone: item.watched,
      hasAttachments: item.assets.length,
      hasQuiz: item.questionnaireId,
      questionnaireId: item.questionnaireId,
      certificate: {
        url: item.certificateUrl
      },
      chatId: item.chatId
    },
    seasons: item.seasons,
    attachments: item.assets,
    quizzes: quizArray,
    access: {
      ppc: item.playPermissionCode,
      message: ''
    },
    inFavorites: !!item.inPlaylist
  };
}

function episodeDetailMapper(item) {
  if (item.assets) {
    item.assets.forEach((item) => {
      assetsArray.name = item.name;
      assetsArray.url = item.url;
      assetsArray.id = item.id;
    });
  } else {
    item.assets = [];
  }

  return {
    id: item.id,
    title: item.name,
    descriptions: {
      short: item.shortDescription,
      long: item.description
    },
    genres: item.genres,
    images: {
      thumbs: {
        portrait: [item.featuredImage],
        landscape: [item.regularImage, item.carouselImage]
      },
      featured: {
        portrait: '',
        landscape: item.backgroundImage
      }
    },
    published: item.year,
    availability: {
      plans: item.plans,
      rent: item.price || 0
    },
    specifics: {
      duration: item.duration,
      ageRating: item.rating,
      cast: item.cast,
      svod: item.svod,
      tvod: item.tvod,
      contentType: item.contentType,
      hasTeaser: item.hasTeaser,
      teaserBaseUrl: item.teaserBaseUrl,
      isHappening: item.isHappening,
      timeToNextEvent: item.timeToNextEvent,
      playedRatio: item.watchedPercentage / 100,
      allDone: item.watched,
      hasAttachments: item.assets.length,
      hasQuiz: item.questionnaireId,
      certificate: {
        url: item.certificateUrl
      },
      chatId: item.chatId
    },
    seasons: item.seasons,
    attachments: assetsArray,
    quizzes: quizArray,
    access: {
      ppc: item.playPermissionCode,
      message: ''
    },
    inFavorites: !!item.inPlaylist,
    seasonNumber: item.seasonNumber
  };
}

function categoryMapper(item) {
  return {
    id: item.categoryId,
    type: item.typeCategory, // convert from enum,
    title: item.name,
    description: ''
  };
}

function channelMapper(item) {
  const images = {
    thumbs: {
      portrait: [],
      landscape: []
    },
    featured: {
      portrait: '',
      landscape: ''
    }
  };

  if (item.channelPayload) {
    item.channelPayload.forEach((channel) => {
      switch (channel.type) {
        case 'CHANNEL_SPOTLIGHT':
          images.featured.landscape = channel.url || '';
          break;
        case 'CHANNEL_THUMBNAIL_MO':
          if (channel.url) images.thumbs.portrait.push(channel.url);
          break;
        case 'CHANNEL_THUMBNAIL':
          if (channel.url) images.thumbs.landscape.push(channel.url);
          break;
        case 'CHANNEL_MOBILE':
          images.featured.portrait = channel.url || '';
          break;
        case 'CHANNEL_SELECTOR_MOBILE':
          images.thumbs.portrait.push(channel.url);
          break;
        case 'CHANNEL_SELECTOR_WEB':
          if (channel.url) images.thumbs.landscape.push(channel.url);
          break;
      }
    });
  }

  return {
    id: item.id,
    title: item.name,
    description: item.description,
    images,
    link: `/channel/${item.id}`
  };
}

export default (axios) => ({
  /**
   * Gets the listing of titles that match the search term.
   * @param {String} distributorId
   * @param {String} userToken User token as informed by server on authentication.
   * @param {String} term Text filled by user in search form.
   * @param {Number} pageNumber Page Number.
   * @param {Number} pageSize Page Size.
   * @returns {Promise}
   */
  search(distributorId, userToken, term, pageNumber, pageSize) {
    return axios({
      method: 'get',
      url: `distributor/${distributorId}/content/search/${term}/${pageNumber}/${pageSize}?usePPC=true`,
      headers: {
        utoken: userToken
      }
    }).then(function (response) {
      const contents = [];
      response.data.content.forEach((item) => {
        contents.push(contentMapper(item));
      });
      return Promise.resolve({
        code: 0,
        message: 'OK, here are the contents that I found.',
        data: {
          total: response.data.totalElements || 0,
          matches: contents
        }
      }).catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
    });
  },

  /**
   * Gets the blocks that are parte of a page. This method must be called by pages that show various lists of titles, like home.
   * The blocks are shown in page in the order set in _blocks_ array.
   * The following types are supported (please see XD prototype of home page):
   * * SHOWCASE = List of featured titles. Usually is the first element in home page.
   * * TITLES = Regular list of titles. Usually show in carousel.
   * * TITLES_FEATURED = Featured list of titles.
   * * TITLES_ALT = Alternative list of titles.
   * * GROUPS = List of titles groups, like channels for instance. Theme will show this list different from regular titles list.
   * * FEATURED_SERIES = Featured list of groups, like seasons of a tv series.
   * * GROUP_OF_GROUPS_FEATURED = Featured list of groups, like playlists or any other content's grouping.
   * * AD-GOOGLE = Advertising slot that will be filled by Google service.
   * * FEATURED_TITLE = A single title that must be shown as featured.
   * All blocks must have and _id_ property that will be used as parameter in further requests to get content.
   * @param {Number} channelId
   * @param {String} userToken User token as informed by server on authentication.
   * @returns {Promise<{code: number, data: {blocks: Array}, message: string}>}
   */
  getPageLayout(userToken, channelId) {
    return axios({
      method: 'get',
      url: `categories/order/content?channelId=${channelId}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const blocks = [];
        response.data.forEach((item) => {
          blocks.push(categoryMapper(item));
        });
        return Promise.resolve({
          code: 0,
          message: 'OK, here are the data to show you how to layout the page.',
          data: {
            blocks
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets the list of titles of a given group id.
   * Titles' structure is the same of _search_ method.
   * @param {String} distributorId
   * @param {Number} categoryId ID de uma categoria.
   * @param {String} userToken User token as informed by server on authentication.
   * @param {Number} pageNumber Page Number.
   * @param {Number} pageSize Page Size.
   * @param {Number} [channelId] Channel ID.
   * @returns {Promise<{code: number, data: Object, message: string}>}
   */
  getTitlesList(
    distributorId,
    categoryId,
    userToken,
    pageNumber,
    pageSize,
    channelId
  ) {
    let url = `distributor/${distributorId}/content/carousel/${categoryId}/${pageNumber}/${pageSize}?`;
    if (channelId) {
      url += `channelId=${channelId}&`;
    }
    url += `usePPC=true`;
    return axios({
      method: 'get',
      url,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const d = response.data;
        const titles = [];
        d.content.forEach((item) => {
          titles.push(contentMapper(item));
        });
        return Promise.resolve({
          code: 0,
          message: 'OK, here are the contents that I found.',
          data: {
            total: d.totalElements,
            titles
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets the list of courses list.
   * @param {String} distributorId
   * @param {String} userToken User token as informed by server on authentication.
   * @param {Number} pageNumber Page Number.
   * @param {Number} pageSize Page Size.
   * @returns {Promise<{code: number, data: Object, message: string}>}
   */
  getNewCoursesList(distributorId, userToken, pageNumber, pageSize) {
    const url = `distributor/${distributorId}/content/newcourses/${pageNumber}/${pageSize}?usePPC=true`;

    return axios({
      method: 'get',
      url,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const d = response.data;
        const titles = [];

        d.content.forEach((item) => {
          titles.push(contentMapper(item));
        });
        return Promise.resolve({
          code: 0,
          message: 'OK, here are the contents that I found.',
          data: {
            total: d.totalElements,
            titles
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets the list of titles related to title with given ia.
   * @param {String} distributorId
   * @param {String} userToken User token as informed by server on authentication.
   * @param {String} titleId Id of title.
   * @returns {Promise<{code: number, data: {total: number, titles: Array}, message: string}>}
   */
  getRelatedTitles(distributorId, userToken, titleId) {
    return axios({
      method: 'get',
      url: `distributor/${distributorId}/content/carousel/related/${titleId}?usePPC=true`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const titles = [];
        response.data.forEach((item) => {
          titles.push(contentMapper(item));
        });
        return Promise.resolve({
          code: 0,
          message: 'OK, here are the contents that I found.',
          data: {
            total: response.totalElements,
            titles
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets details of a title.
   * @param {String} distributorId
   * @param {String} userToken User token as informed by server on authentication.
   * @param {String} titleId Id of title.
   * @returns {Promise<{code: number, data: {related: Array, published: string, availability: {plans: [], buy: number, rent: number}, specifics: [{name: string, value: string}, {name: string, value: string}, {name: string, value: string[]}, {name: string, value: boolean}, {name: string, value: boolean}, null]}, message: string}>}
   */
  getTitleDetails(distributorId, userToken, titleId) {
    return axios({
      method: 'get',
      url: `distributor/${distributorId}/content/single/${titleId}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        return {
          code: 0,
          message: 'OK',
          data: contentDetailMapper(response.data)
        };
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets the contents of a list. Like episodes of a series season.
   * @param {Number|String} distributorId
   * @param {String} userToken
   * @param {Number|String} seasonId
   * @returns {Promise}
   */
  getContentsOfList(distributorId, userToken, seasonId) {
    return axios({
      method: 'get',
      url: `distributor/${distributorId}/content/episodes/${seasonId}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const episodes = [];
        response.data.forEach((item) => {
          episodes.push(episodeDetailMapper(item));
        });
        return {
          data: episodes
        };
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Get the groups of a list with given id.
   * @param {String} userToken User token as informed by server on authentication.
   * @returns {Promise<{code: Number, message: String, data: Object}>}
   */
  getGroupsList(userToken) {
    return axios({
      method: 'get',
      url: `channels/order`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const channels = [];
        const selectorChannels = [];
        const d = response.data;
        if (d.channelSelectorActive === true) {
          (d.selectorOrder || []).forEach((item) => {
            selectorChannels.push(channelMapper(item));
          });
        }
        (d.menuOrder || []).forEach((item) => {
          channels.push(channelMapper(item));
        });
        return Promise.resolve({
          code: 0,
          message: 'OK',
          data: {
            channelSelectorActive: d.channelSelectorActive,
            channels,
            selectorChannels
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Get list of titles in user's favorites list.
   * @param {String} distributorId
   * @param {String} userToken User token as informed by server on authentication.
   * @param {Number} pageNumber Page Number.
   * @param {Number} pageSize Page Size.
   * @returns {Promise<{code: number, data: {total: number, titles: [{images: {featured: {portrait: string, landscape: string}, thumbs: {portrait: string[], landscape: string[]}}, access: {canSubscribe: boolean, canRent: boolean, granted: boolean, canBuy: boolean}, genres: string[], inFavorites: boolean, id: string, title: string, descriptions: {short: string, long: string}, userRating: number}]}, message: string}>}
   */
  getFavorites(distributorId, userToken, pageNumber, pageSize) {
    return axios({
      method: 'get',
      url: `distributor/${distributorId}/content/favorites/${pageNumber}/${pageSize}?usePPC=true`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const titles = [];
        if (response?.data?.content?.length) {
          response.data.content.forEach((item) => {
            titles.push(contentMapper(item));
          });
        }
        return Promise.resolve({
          data: {
            total: response.data.totalElements,
            titles
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets a list of titles purchased or rented by user.
   * @param {String} distributorId
   * @param {String} userToken
   * @param {Number} offset Start index. For pagination.
   * @returns {Promise<{code: number, data: {total: number, titles: [{images: {featured: {portrait: string, landscape: string}, thumbs: {portrait: string[], landscape: string[]}}, access: {canSubscribe: boolean, canRent: boolean, granted: boolean, canBuy: boolean}, genres: string[], inFavorites: boolean, id: string, acquisition: {datetime: string, method: string, transactionId: string}, title: string, descriptions: {short: string, long: string}, userRating: number}]}, message: string}>}
   */
  // TODO hoje só temos o historico de pagamentos
  /*
  getPurchases: function (distributorId, userToken, offset) {
    return Promise.resolve({
      code: 0,
      message: 'OK, here are all content purchased or rented by user.',
      data: {
        total: 32,
        titles: [
          {
            id: '5244',
            title: 'A morte de Thomas e Martha Wayne',
            descriptions: {
              short: '1997, cartoon',
              long: 'Oi, eu sou a descrição desse vídeo que é muito legal. Clica aí, veja e seja feliz.'
            },
            genres: ['infantil', 'animado'],
            userRating: 4,
            images: {
              thumbs: {
                portrait: ['https://vspprod30-cdn.vizzto.com.br/content/252/images/752.jpg'],
                landscape: ['https://vspprod30-cdn.vizzto.com.br/content/252/images/753.jpg']
              },
              featured: {
                portrait: 'https://vspprod30-cdn.vizzto.com.br/content/252/images/750.jpg',
                landscape: 'https://vspprod30-cdn.vizzto.com.br/content/252/images/751.jpg'
              }
            },
            access: {
              granted: true,
              canSubscribe: false,
              canRent: false,
              canBuy: false
            },
            inFavorites: true,
            acquisition: {            // Acquisition data
              method: 'PURCHASED',    // PURCHASED, RENTED or GIFTED
              datetime: '2021-12-26T18:55:48',
              transactionId: 'fasdfasf-fasdfa-r-fff'
            }
          }
        ]
      }
    })
  },
  */

  /**
   * Get list of courses that user bought.
   * @param {String} distributorId
   * @param {String} userToken
   * @param {Number} pageNumber
   * @param {Number} pageSize
   * @returns {Promise}
   */
  getAcquiredCourses(distributorId, userToken, pageNumber, pageSize) {
    return axios({
      method: 'get',
      url: `/distributor/${distributorId}/content/acquired/${pageNumber}/${pageSize}?usePPC=true&courses=true`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const titles = [];
        if (response?.data?.content?.length) {
          response.data.content.forEach((item) => {
            titles.push(contentMapper(item));
          });
        }
        return Promise.resolve({
          data: {
            total: response.data.totalElements,
            titles
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Get list of courses from user that are in progress.
   * @param {String} distributorId
   * @param {String} userToken
   * @param {Number} pageNumber
   * @param {Number} pageSize
   * @returns {Promise}
   */
  getInProgressCourses(distributorId, userToken, pageNumber, pageSize) {
    return axios({
      method: 'get',
      url: `/distributor/${distributorId}/content/watching/${pageNumber}/${pageSize}?usePPC=true&courses=true`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const titles = [];

        if (response?.data?.content?.length) {
          response.data.content.forEach((item) => {
            titles.push(contentMapper(item));
          });
        }

        return Promise.resolve({
          data: {
            total: response.data.totalElements,
            titles
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Get user's concluded courses with certificates.
   * @param {String} distributorId
   * @param {String} userToken
   * @param {Number} pageNumber
   * @param {Number} pageSize
   * @returns {Promise}
   */
  getConcludedCourses(distributorId, userToken, pageNumber, pageSize) {
    /*
      Concluded courses must have the following properties:
      - specifics.playlistLength    Number of classes in the course. <- esta informação ainda nao esta no cache, por tanto ainda nao implementada pelo back
      - specifics.certificate.url   Url of certificate in pdf.
    */
    return axios({
      method: 'get',
      url: `/distributor/${distributorId}/content/watched/${pageNumber}/${pageSize}?usePPC=true&courses=true&certificate=true`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const titles = [];
        if (response?.data?.content?.length) {
          response.data.content.forEach((item) => {
            titles.push(contentMapper(item));
          });
        }
        return Promise.resolve({
          data: {
            total: response.data.totalElements,
            titles
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Buy or rent content.
   * @param {String} userToken
   * @param {String} deviceManagementId
   * @param {Number|String} contentId
   * @param {Number} contentPrice
   * @param {String} purchasePointLocation Local originario da transação.
   * @returns {Promise<{code: number, data: {validUntil: string, transactionId: string}, message: string}>}
   */
  acquireContent(
    userToken,
    deviceManagementId,
    contentId,
    contentPrice,
    purchasePointLocation
  ) {
    return axios({
      url: 'purchases/video',
      method: 'post',
      headers: {
        utoken: userToken
      },
      data: {
        deviceId: deviceManagementId,
        productCode: contentId,
        purchasePointLocation, // 'webapp/#/browse'
        purchaseValue: contentPrice
      }
    })
      .then(function (response) {
        const d = response.data;
        return Promise.resolve({
          data: {
            purchaseId: d.purchaseId,
            status: d.status, // CREATED, PAID, DISPUTED, CANCELED
            statusDescription: d.statusDescription
          },
          code: 0, // Code = 0 means user logged in sucessfully
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  getStatusPurchase(userToken, uname, contentId) {
    return axios({
      method: 'get',
      url: `purchases/status/${uname}/${contentId}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const d = response.data;
        return Promise.resolve({
          code: 0,
          message: d.statusDescription || 'Request done.',
          data: {
            purchaseId: d.purchaseId,
            status: d.status, // CREATED, PAID, DISPUTED, CANCELED
            statusDescription: d.statusDescription
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Get content lists.
   * Content can have different content types. It can have reference to categories, other lists of contents.
   * @param {Number|String} distributorId
   * @param {String} userToken
   * @param {Number|String} groupId Id of group to get content, can be genre id, for instance.
   * @param {String} contentType
   * @param {Number} pageNumber
   * @param {Number} pageSize
   * @returns {Promise}
   */
  getContentLists(
    distributorId,
    userToken,
    groupId,
    contentType,
    pageNumber,
    pageSize
  ) {
    let url = `distributor/${distributorId}/content/carousel/genre`;
    if (groupId) {
      url += `/${groupId}`;
    }
    url += `/type/${contentType}/${pageNumber}/${pageSize}?usePPC=true`;
    return axios({
      method: 'get',
      url,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const d = response.data;
        const contents = d.content.map((item) => contentMapper(item));
        return Promise.resolve({
          code: 0,
          message: response.statusText,
          data: {
            total: d.totalElements || 0,
            titles: contents
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  addFavorite(userToken, contentId) {
    return axios({
      method: 'put',
      url: `content/favorites/${contentId}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        return Promise.resolve({
          data: response.data
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  deleteFavorite(userToken, contentId) {
    return axios({
      method: 'delete',
      url: `content/favorites/${contentId}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        return Promise.resolve({
          data: response.data
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },
  /**
   * Get Content Url
   * @param {String} distributorId
   * @param {String} userToken User token as informed by server on authentication.
   * @param {String} videoId Id of video.
   * @param {String} startIndex start time.
   * @param {String} deviceId deviceId.
   * @returns {Promise<{code: number, data: {total: number, titles: Array}, message: string}>}
   */
  getContentUrl(distributorId, userToken, videoId, startIndex, deviceId) {
    return axios({
      method: 'get',
      url: `distributor/${distributorId}/content/url/${videoId}/startindex/${startIndex}?deviceId=${deviceId}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        return Promise.resolve({
          code: 0,
          message: 'OK, is content load',
          data: response.data
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  getHeartbeatStartIndex(distributorId, userToken, videoId, deviceId) {
    return axios({
      method: 'get',
      url: `distributor/${distributorId}/content/${videoId}/heartbeat`,
      headers: {
        utoken: userToken
      },
      params: {
        deviceId
      }
    })
      .then(function (response) {
        return Promise.resolve({
          code: 0,
          message: 'OK, is data load',
          data: response.data
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },
  getHeartbeatUpdateAccess(
    viewId,
    userToken,
    duration_body_params,
    duration_query_params
  ) {
    return axios({
      method: 'put',
      url: `accesses/heartbeat/${viewId}`,
      headers: {
        utoken: userToken
      },
      params: duration_query_params,
      data: duration_body_params
    })
      .then(function (response) {
        return Promise.resolve({
          code: 0,
          message: 'OK, is data load',
          data: response.data
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets the list of suggestions for user
   * @param {String} distributorId
   * @param {String} userToken User token as informed by server on authentication.
   * @returns {Promise<{code: number, data: {total: number, suggestions: Array}, message: string}>}
   */
  getSuggestions(distributorId, userToken) {
    return axios({
      method: 'get',
      url: `distributor/${distributorId}/content/carousel/suggestion`,
      // get /distributor/{distributorId}/content/carousel/suggestion
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const suggestions = [];
        response.data.forEach((item) => {
          suggestions.push(contentMapper(item));
        });
        return Promise.resolve({
          code: 0,
          message: 'OK, here are the contents that I found.',
          data: {
            total: response.totalElements,
            suggestions
          }
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets details of a course.
   * @param {String} distributorId
   * @param {String} userToken User token as informed by server on authentication.
   * @param {String} titleId Id of title.
   * @returns {Promise<{code: number, data: {related: Array, published: string, availability: {plans: [], buy: number, rent: number}, specifics: [{name: string, value: string}, {name: string, value: string}, {name: string, value: string[]}, {name: string, value: boolean}, {name: string, value: boolean}, null]}, message: string}>}
   */
  getCourseDetails(distributorId, userToken, titleId) {
    return axios({
      method: 'get',
      url: `distributor/${distributorId}/content/single/${titleId}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        return {
          code: 0,
          message: 'OK',
          data: contentMapper(response.data)
        };
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  joinCourseClass({ utoken, videoId }) {
    console.log(videoId);

    return axios({
      method: 'post',
      url: `subscriptions/video/${videoId}/join`,
      headers: {
        utoken
      }
    })
      .then(() => ({
        code: 0,
        message: 'OK',
        data: {}
      }))
      .catch((error) => Promise.reject(ResponseHelper.getErrorResponse(error)));
  }
});
